import { useCallback, useEffect, useMemo, useRef } from "react";
import confetti from "canvas-confetti";
import { playCelebrationSound } from "./celebrationAudio";
import {
  fireAchievementConfetti,
  fireBlockGeneratedConfetti,
  fireLegacyBurst,
  getPerformanceScale,
} from "./celebrationConfetti";
import type { CelebrationOptions } from "./celebration.types";
import { clearWindowTimer } from "./celebrationTimers";
import { fireLegacyFireworks } from "./legacyCelebrationConfetti";

type UseCelebrationEffectsOptions = {
  muted: boolean;
  reducedMotion: boolean;
};

export function useCelebrationEffects({
  muted,
  reducedMotion,
}: UseCelebrationEffectsOptions) {
  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const confettiInstanceRef = useRef<confetti.CreateTypes | null>(null);
  const pendingEffectsRef = useRef<CelebrationOptions | null>(null);
  const hasUserInteractionRef = useRef(false);
  const fireworksTimerRef = useRef<number | null>(null);
  const perfScale = useMemo(() => getPerformanceScale(), []);

  useEffect(() => {
    if (!canvasRef.current) return;
    confettiInstanceRef.current = confetti.create(canvasRef.current, {
      resize: true,
      useWorker: true,
    });

    return () => {
      clearWindowTimer(fireworksTimerRef);
      confettiInstanceRef.current?.reset();
    };
  }, []);

  useEffect(() => {
    if (!muted && !reducedMotion) return;
    clearWindowTimer(fireworksTimerRef);
    confettiInstanceRef.current?.reset();
  }, [muted, reducedMotion]);

  useEffect(() => {
    const markUserInteraction = () => {
      hasUserInteractionRef.current = true;
    };

    window.addEventListener("pointerdown", markUserInteraction, { passive: true });
    window.addEventListener("keydown", markUserInteraction, { passive: true });
    window.addEventListener("touchstart", markUserInteraction, { passive: true });

    return () => {
      window.removeEventListener("pointerdown", markUserInteraction);
      window.removeEventListener("keydown", markUserInteraction);
      window.removeEventListener("touchstart", markUserInteraction);
    };
  }, []);

  const runEffects = useCallback(
    (options: CelebrationOptions) => {
      const fire = confettiInstanceRef.current;
      if (!fire) {
        pendingEffectsRef.current = options;
        return;
      }

      if (!muted && !reducedMotion) {
        if (options.type === "block-generated") {
          fireBlockGeneratedConfetti(fire, perfScale);
        } else if (options.type === "achievement-unlocked") {
          fireAchievementConfetti(fire, perfScale);
        } else if (options.type === "burst") {
          fireLegacyBurst(fire, options.intensity, perfScale);
        } else {
          fireLegacyFireworks(fire, options.intensity, perfScale, fireworksTimerRef);
        }
      }

      playCelebrationSound({
        hasUserInteraction: hasUserInteractionRef.current,
        muted,
        reducedMotion,
        sound:
          options.type === "achievement-unlocked" || options.type === "fireworks"
            ? "achievementUnlocked"
            : "blockGenerated",
        volume:
          options.type === "achievement-unlocked" || options.type === "fireworks"
            ? 0.24
            : 0.16,
      });
    },
    [muted, perfScale, reducedMotion],
  );

  useEffect(() => {
    const pending = pendingEffectsRef.current;
    if (!pending || !confettiInstanceRef.current) return;
    pendingEffectsRef.current = null;
    runEffects(pending);
  }, [runEffects]);

  return { canvasRef, runEffects };
}
